
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';
import { Company } from '@/types/company';
import { Skeleton } from '@/components/ui/skeleton';
import BrandingForm from '@/components/settings/BrandingForm';
import CompanyForm from './forms/CompanyForm';

export function CompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [isSaving, setIsSaving] = useState(false);

  // Cargar empresa desde Supabase
  const fetchCompany = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          company_themes (*),
          company_domains (*)
        `)
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      
      // Transformar los datos al formato de Company
      const formattedCompany: Company = {
        id: data.id,
        name: data.name,
        description: data.description || '',
        status: data.status,
        contactEmail: data.contact_email,
        contactPerson: data.contact_person,
        domains: data.company_domains.map(d => ({
          id: d.id,
          domain: d.domain,
          companyId: d.company_id,
          isPrimary: d.is_primary,
          isActive: d.is_active,
          createdAt: new Date(d.created_at)
        })),
        theme: data.company_themes[0] ? {
          primaryColor: data.company_themes[0].primary_color || '#1E8449',
          secondaryColor: data.company_themes[0].secondary_color || '#F1C40F',
          accentColor: data.company_themes[0].accent_color || '#2C3E50',
          logoUrl: data.company_themes[0].logo_url || null,
          fontPrimary: data.company_themes[0].font_primary || null,
          fontSecondary: data.company_themes[0].font_secondary || null
        } : {
          primaryColor: '#1E8449',
          secondaryColor: '#F1C40F',
          accentColor: '#2C3E50'
        },
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      
      setCompany(formattedCompany);
    } catch (error) {
      console.error('Error al cargar empresa:', error);
      toast.error('Error al cargar la empresa');
      navigate('/admin/companies');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar empresa al montar el componente
  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  const handleUpdateCompanyDetails = async (updatedData: Partial<Company>) => {
    if (!company) return;
    
    setIsSaving(true);
    try {
      // Actualizar empresa en Supabase
      const { error } = await supabase
        .from('companies')
        .update({
          name: updatedData.name,
          description: updatedData.description,
          status: updatedData.status,
          contact_email: updatedData.contactEmail,
          contact_person: updatedData.contactPerson
        })
        .eq('id', company.id);
      
      if (error) throw error;
      
      // Actualizar empresa en el estado
      setCompany({
        ...company,
        ...updatedData
      });
      
      toast.success('Empresa actualizada con éxito');
    } catch (error) {
      console.error('Error al actualizar empresa:', error);
      toast.error('Error al actualizar la empresa');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/3" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Empresa no encontrada</h1>
        </div>
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">La empresa no pudo ser encontrada. Verifique el ID e intente de nuevo.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/companies')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{company.name}</h1>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/companies/${company.id}/domains`)}
          >
            <Globe className="mr-2 h-4 w-4" />
            Gestionar Dominios
          </Button>
        </div>
      </div>

      <Tabs
        defaultValue={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="details">Detalles</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Información de la empresa</CardTitle>
              <CardDescription>
                Edita los detalles básicos de la empresa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CompanyForm 
                company={company}
                onSubmit={handleUpdateCompanyDetails}
                isSaving={isSaving}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
              <CardDescription>
                Personaliza los colores, logo y estilo visual de la empresa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BrandingForm company={company} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
